'use client';
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useRouter } from "next/router";
import LineItem from "./LineItem"
import { useShopify } from "../../hooks/useShopify";
import { CartInfo, fetchCartDetails, getUserCartInfo, getUserInfo, updateCartData } from "@/utils/useUserInfo";
import { CART_TOKEN, GCART_TOKEN, deepEqual, getLoggedInEmailAddress, gtmClickTracking, navigateAndReplaceHistory, navigateWithHistory } from "@/utils";
import cssStyles from "./shopify_cart.module.scss";
import Button from '@mui/material/Button';
import { useSearchParams } from 'next/navigation'
import Campaign from "@/store/campaign";
import { getProductData, getProductsByIds } from "@/store/productSlice";
import InventoryAlert from "./unavailableItems";

const ShopifyCart: React.FC = (props:any) => {

	console.log("----- Shopify-Cart Start--------");

	let [userInfo, setUserInfo] = useState(null as any);
	let [isUserInfoError, setUserInfoError] = useState(null as any);
	const brand = props.brand || "";
	const checkoutId = useRef(null);
	const isGuestCheckoutCreated = useRef(false);
	const [isVariantsAdded,setIsVariantsAdded] = useState(false);
	const [cartItem,setCartItem] = useState(null);
	const isCartItemDeleted = useRef(false);
	const router = useRouter();
	const searchParams = useSearchParams()
	const moveToCheckout = useRef(searchParams.get('next') as string === "checkout" ? true : false);
	const [updateToInventoryQuantity, setUpdateToInventoryQuantity] = useState(false);//update to iventory quantity from db
    const[cartMerging, setCartMerging] = useState(false);
    const[loggedInUserUpdating, setLoggedInUserUpdating] = useState(false);
    const[canInitiateCartMerge, setCanInitiateCartMerge] = useState(false);
    const[cartMerged, setCartMerged] = useState(false);
	const [productData, setProductData]= useState([]);//inventory data with product and its quantity values
	
	const {
		getClient,
		cartStatus,
		createNewCart,
		dbAddressInfo,
		cartCount,
		fetchProducts,
		skusCommaSeperated,
		checkoutState,
		guestCheckoutState,
		createCheckout,
		addVariant,
		setCount,
		fetchShippingAddressInShopify,
		saveShippingAddressInShopify,
		saveEmailAddressInShopify
	} = useShopify();


	/**
	 * fetch the db inventory available count and use it for not updating the quantities beyond availability
	 */
	useEffect(()=>{
		if(skusCommaSeperated){
			(async ()=>{
				const productData = await getProductsByIds( {productIds: skusCommaSeperated} );
				console.log(productData);
				setProductData(productData?.products?.map((eachProduct:any)=>{   
					return { 
						productImage: eachProduct.productImage,
						availabilityCount: eachProduct.availabilityCount, 
						productId:eachProduct.productId,
						productName: eachProduct.productName 
					}
				}))
			})();
		}
	},[checkoutState])


	useEffect(()=>{
		const userInfoPropValue = props.userInfo;
		if(userInfoPropValue){
			setUserInfo((prevUserInfo:any) => {
				if(deepEqual(prevUserInfo,userInfoPropValue)){
					return prevUserInfo;
				}
				return userInfoPropValue;
			});
		}
	},[props.userInfo]);

	function upatedIfDeletedCartItem(value:boolean=false){
		isCartItemDeleted.current=value;
	}


	async function updateCartInfoIfloggedIn() {
		try{
			//always a fresh call or sessionStorage value of customerId is checked and 
			//if present then only cart info is tried to save
			const userInfo = await getUserInfo() as any;
			//for logged-out flow userInfo will not present {userInfo:{unauthorized: true}}, 
			//so making getUserInfo() request here considering the different tabs concept

			if (userInfo && userInfo.cId && getLoggedInEmailAddress() && !loggedInUserUpdating) {
			    setLoggedInUserUpdating(true);
				setCanInitiateCartMerge(false);
				const cartData: CartInfo = await getUserCartInfo(userInfo);
                console.log('-------cartData---------', cartData);
				if(cartData){
					if(cartData.error){
						return setUserInfoError(true)
					}
					try{	
						//update the cart Data with even CartCount 0
						await updateCartData(userInfo,checkoutState,cartItem, setCartItem, true,isCartItemDeleted.current, upatedIfDeletedCartItem, createNewCart);
					}catch(e){
						console.log("Failed Saving the cartData",e)
					}

					setUserInfo((prevUserInfo:any) => {
						if(deepEqual(prevUserInfo,userInfo)){
							return prevUserInfo;
						}
						return userInfo;
					});
					setCartCookie(cartData.cartId);
					isGuestCheckoutCreated.current = false;
				}
				setLoggedInUserUpdating(false);
				setCanInitiateCartMerge(true);
			}
		}catch(e){
			console.log("Failed Getting the userInfo",e)
		}
		return null;
	}

	useEffect(() => {
	    async function doIt() {
            //update the cart only when checkoutState is filled with content
            if(checkoutState && Object.keys(checkoutState).length > 0 && getLoggedInEmailAddress()){
                console.log("----- Shopify-Cart---------", checkoutState)
                await updateCartInfoIfloggedIn();
            }
		}
		doIt();
	},[checkoutState])

	useEffect(() => {
        console.log("🛒 [Cart API v3.0] Cart navigation check:", {
			hasCheckoutState: checkoutState && Object.keys(checkoutState).length > 0,
			isLoggedIn: !!getLoggedInEmailAddress(),
			cartMerged: cartMerged,
			webUrl: checkoutState?.webUrl,
			moveToCheckout: moveToCheckout.current
		});
        if(checkoutState && Object.keys(checkoutState).length > 0 && getLoggedInEmailAddress() && cartMerged){
			//if cart is merged then clear the guest cart cookie, irrespective of move to checkout
			console.log("🛒 [Cart API v3.0] Cart merged successfully, clearing guest cart cookie");
			emptyGuestCartCookie();
            if(moveToCheckout.current){
				console.log("🛒 [Cart API v3.0] Navigating to checkout:", checkoutState.webUrl);
                setTimeout(()=>{
                    navigateWithHistory(router,checkoutState.webUrl);
                },100);
            }
        }
	},[checkoutState, cartMerged])

	useEffect(()=>{
		if(checkoutState && Object.keys(checkoutState).length > 0 && userInfo){
			(async () => {
				try{
					saveEmailAddressInShopify({ checkoutId: checkoutState.id, emamilAddress: getLoggedInEmailAddress() })
					await fetchShippingAddressInShopify({customerId: userInfo.cId});
				}catch(e){}
			})()
		}
	},[checkoutState, userInfo])


	useEffect(()=>{
		if(dbAddressInfo && checkoutState && Object.keys(checkoutState).length > 0){
			saveShippingAddressInShopify({checkoutId : checkoutState.id, shippingAddress: dbAddressInfo})
		}
	},[dbAddressInfo])

	useEffect(()=>{
		if(isVariantsAdded){
			fetchProducts();
		}
	},[isVariantsAdded])


	useEffect(() => {

		/**
		 *
		 * GCART_TOKEN - Guest Cart token cookie name variable
		 * CART_TOKEN - Logged In Cart Token cookie name variable
		 */

		/**
		 * 
		 * LoggedIn User - 
		 * 
		 * Case 1: CART_TOKEN cookie present - will fetch the user specific cart
		 * 
		 * Case 2: CART_TOKEN cookie not present && userInfo(contains CartId) yet to come -
		 * we will wait until then user specific API returns cartId and then based on checkoutState, cart items will be shown
		 * NOTE: we will save the user cartId in CART_TOKEN cookie once on every checkoutState is retrieved and DB Cart info is updated
		 * 
		 * Case 3: CART_TOKEN and GCART_TOKEN both present - 
		 * after merge guest cart items to loggedIn cart then emptyGuestCartCookie and 
		 * fetch products based on (isVariantsAdded)
		 * 
		 * Case 4: if 
		 * 			i) CART_TOKEN  not present and  
		 * 			ii) userInfo / DB CartId data not present(FirstTime user) or
		 * 				( FAILURE CASE of userInfo-API OR OUR-CART-API calls for existing USER) and 
		 * 		   iii) GCART_TOKEN not present - (on direct landing on cart)
		 * 
		 * 		UI : only EMPTY CART msg and SHOP button is shown here 
		 * 			--- (guest Shopify checkout  will be created to show EMPTY cart)  
		 * 				IN cart page only that guest cartId is saved to CART_TOKEN cookie and updated in DB (only if CustomerId is present)---
		 * 				
		 * 			(else if direct landing on PDP)
		 * 			--- IN PDP only we will save that guestToken as CART_TOKEN and in Cart page we update the DB (only if CustomerId is present)---
		 * 
		 * 			USER ACTION:
			* 		 STEP 1: clicks on SHOP button and now goes back to rooms and 
			* 		 STEP 2: comes to PDP after choosing product, 
			* 		 STEP 3: In PDP page clicks on AddToCART button then CART_TOKEN is created based on loggedIn Flow
		 *
		 * 
		 */

		/**
		 * LoggedOut User -
		 * 
		 * Case 1: CART_TOKEN cookie will not be present or will be "null" - 
		 * if null string consider as guest user - only EMPTY CART msg and SHOP button is shown here 
		 * --- IN PDP only we will save the GCART_TOKEN (based on above USER ACTION)--- 
		 * 
		 * Case 2: if no CART_TOKEN don't update / delete CART items (loggedIn in one tab and loggedOut in another tab)
		 */

		if(!userInfo?.cId){
		 	return;//in _app.tsx we will make api call to get the userInfo object and send it as props to this component
		}

		console.log("userInfo=",userInfo);

//		if (getLoggedInEmailAddress()) {
//            props.setLoginEl(null)
//        }else{
//            props.setLoginEl(document.getElementById("sign_in"));
//            return;
//        }

		(async ()=>{
			let cartData = await getUserCartInfo(userInfo);
			if (typeof window !== 'undefined') {
				
				
				//@ts-ignore
				let loggedInUserCartId = cartData?.cartId || window.getCookie(CART_TOKEN);
				//@ts-ignore
				let guestCartId = window.getCookie(GCART_TOKEN);
	
				loggedInUserCartId = (loggedInUserCartId == "null" || loggedInUserCartId == "undefined") ? null: loggedInUserCartId;
				guestCartId = (guestCartId == "null" || guestCartId == "undefined") ? null: guestCartId;
	
				//if logged In User, then we are creating guest cart seperately and also creating loggedInUserCart to merge
				if(guestCartId && loggedInUserCartId){	
					isGuestCheckoutCreated.current = true;
					createCheckout({checkoutId: guestCartId, guest: true});
				}
				//else if not logged in then loggedInUserCartId will be empty and only guestCart will be created
				//@ts-ignore
				checkoutId.current = loggedInUserCartId || guestCartId;
			}
			createCheckout({checkoutId: checkoutId.current});
			
			fetchProducts();
		})()

		//@ts-ignore
		
	},[userInfo])


	useEffect(() => {
		//after user logs in----
		//if already logged in User then directly merge and re-render the DOM happens once checkoutState changes
		const emailAddress = getLoggedInEmailAddress();
		if(emailAddress && isGuestCheckoutCreated.current) {
            if ((window as any).getCookie(GCART_TOKEN) == (window as any).getCookie(CART_TOKEN)) {
                console.log("----- Shopify-Cart guest token and cart token are the same, clearing guest token cookie--------");
                emptyGuestCartCookie();
            } else {
                console.log("----- Shopify-Cart merging guest cart to logged in cart--------");
                (async ()=>{
                    await mergeGuestCartToLoggedInCart();
                })()
			}
		}
	},[guestCheckoutState])




	useEffect(() => {
		console.log("----- Shopify-Cart Start--useEffect init------checkoutState:", checkoutState, "cartStatus:", cartStatus)



		async function getCount() {
			let lineItems =
				checkoutState.lineItems && checkoutState.lineItems.length > 0
					? checkoutState.lineItems
					: []
			let count = 0
			lineItems.forEach((item: { quantity: number }) => {
				count += item.quantity
				return count
			})
			setCount(count);
		}
		if(checkoutState) {
		    getCount();
		}
	}, [cartStatus, checkoutState])


	function continueShopping(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		e.preventDefault();
		window.location.replace(`${brand}/rooms`); // opens checkout in same window
	}

	function mergeGuestCartToLoggedInCart(){
		if(guestCheckoutState && guestCheckoutState.lineItems && !cartMerging && canInitiateCartMerge){
		    setCartMerging(true);
			const guestLineItems = guestCheckoutState.lineItems;
			const guestLineItemsToAdd = guestLineItems.map((item:any) => {
				if(item.variant.id != "no variant Id"){
					return {
						variantId: item.variant.id,
						quantity: item.quantity
					}
				}
			});
			//add all the guest variants to loggedIn user checkout
			addVariant(checkoutId.current, guestLineItemsToAdd, async () => {
				//now freshly get all the new variants
				//cannot call fetch Products here as reducer is still executing till here so getState cannot be called
				//fetchProducts();
				emptyGuestCartCookie()
				setIsVariantsAdded(true);
				console.log("success in adding all the variants of guest to loggedIn user checkout");
			});
            setCartMerging(false);
            setCartMerged(true);
		}
	}

	function emptyGuestCartCookie(){
		if (typeof window !== 'undefined') {
			//@ts-ignore
			window.setCookieWithoutSecure(GCART_TOKEN,null,0);
		}
	}
	

	function setCartCookie(cartId: string|undefined) {
		//re-updating the cart with same cartId with 10 days of expiry time
		if (typeof window !== 'undefined') {
		    cartId = (cartId != undefined) ? cartId: checkoutState.id
			//@ts-ignore
			window.setCookieWithoutSecure(CART_TOKEN,cartId,10);
			//cosidering above guest cart as logged In cart and saving it as main CART_TOKEN
//			if(!isGuestCheckoutCreated.current) {
//				emptyGuestCartCookie();
//			}

		} 
	}

	/**
	 * If LoggedIn -  
	 * and then ONLLY proceed to checkout
	 *  
	 * If LoggedOut - navigate to /login and after loginIn comes back to  this(?next=cart) page.
	 * NOTE: after coming back we check for GCART_TOKEN and merge.
	 * 
 	 * @param e 
	 * @returns 
	 */
	async function openCheckout(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		e.preventDefault();
		console.log("🛒 [Cart API v3.0] Checkout button clicked:", {
			cartId: checkoutState?.id,
			webUrl: checkoutState?.webUrl,
			isLoggedIn: !!getLoggedInEmailAddress(),
			skusCommaSeperated
		});
		gtmClickTracking({ event : 'check_out_clicked', 'productId': skusCommaSeperated});
		//window.location.replace(checkoutState.webUrl) // opens checkout in same window
		setTimeout(()=>{
			const emailAddress = getLoggedInEmailAddress();
			if(!emailAddress){
				console.log("🛒 [Cart API v3.0] User not logged in, redirecting to login");
				return navigateAndReplaceHistory(router,`/login?next=${encodeURIComponent(window.location.href+`?next=checkout`)}`);
			}
			console.log("🛒 [Cart API v3.0] Proceeding to checkout:", checkoutState.webUrl);
			return navigateWithHistory(router,checkoutState.webUrl);
		},5000);
	}

	if(cartCount<=0 ){
		return (<div id="cart">
		    <Campaign campaignName="cart" pageName="cart" />
				{
					(userInfo || userInfo?.unauthorized  || isUserInfoError) ?
					<header className="Cart__header">
						<div className={cssStyles["empty-cart-container"]}>
						{
							(!checkoutState || (checkoutState && checkoutState.lineItems &&
							checkoutState.lineItems.length <=0))?
								<>
								<h2>Empty Cart</h2>
								<Button className={cssStyles["Cart_Continue_shopping"]} variant="contained" color="primary"
									onClick={(e: any) => continueShopping(e)}>
										Continue Shopping
								</Button>
								</>: 
								<>Loading...</>

						}
						</div>
					</header>
					
					: <>Loading...</>
				}
			</div>)
	}

	return (
		<div id="cart">
		    <Campaign campaignName="cart" pageName="cart" />
			<div className={`Cart Cart--open`}>
				<header className={cssStyles['Cart__header']}>
					<h2 >Your cart</h2>
					<InventoryAlert updateToInventoryQuantity={updateToInventoryQuantity} setUpdateToInventoryQuantity={setUpdateToInventoryQuantity} productData={productData} checkoutState={checkoutState}/>
				</header>
				<div className={cssStyles["cart-summary-n-footer"]}>
					<div className={cssStyles["Cart__line-items"]}>
						<LineItem updateToInventoryQuantity={updateToInventoryQuantity} setCartCookie={setCartCookie} productData={productData} cartItem={cartItem} isCartItemDeleted={isCartItemDeleted} userInfo={userInfo} setCartItem={setCartItem} upatedIfDeletedCartItem={upatedIfDeletedCartItem} />
					</div>
					<div className={cssStyles["Cart__footer"]}>
						<h2>Order Summary</h2>
						<div className={cssStyles["cart-all-items-info"]}>
							<span className={cssStyles["subtext-key"]}>Order Subtotal:</span>
							<span className="Cart-info__pricing">
								<span className="pricing">$ {checkoutState.subtotalPrice?.amount}</span>
							</span>
						</div>
						<div className={cssStyles["cart-all-items-info"]}>
							<span className={cssStyles["subtext-key"]}>Shipping:</span>
							<span className="Cart-info__pricing">
								<span className="pricing">Free!</span>
							</span>
						</div>
						<div className={cssStyles["cart-all-items-info"]}>
							<span className={cssStyles["subtext-key"]}>Taxes:</span>
							<span className="Cart-info__pricing">
								<span className="pricing">Calculated on checkout page</span>
							</span>
						</div>
						<div className={cssStyles["cart-all-items-info"]}>
							<span className={cssStyles["subtext-key"]}>Order Total:</span>
							<span className="Cart-info__pricing">
								<span className="pricing">Calculated on checkout page</span>
							</span>
						</div>
						<Button id="check_out_clicked" className={cssStyles['Cart__checkout']} variant="contained" color="primary" 
						onClick={(e:any) => openCheckout(e)}>
							Checkout
						</Button>							
					</div>
				</div>

			</div>
		</div>
	)
}

export default ShopifyCart;
